import { useEffect, type ReactNode } from 'react';
import { notifyHeightChange, postMessageToParent } from '@/lib/utils';
import { WIDGET_MESSAGE_TYPES } from '@/lib/constants';
import { useWidgetSettings, usePropertyContext } from '@/context/PropertyContext';
import { DemoBanner } from './DemoBanner';

interface EmbedLayoutProps {
  children: ReactNode;
}

/**
 * Minimal accessible layout for iframe embed mode
 * 
 * WCAG 2.1 AA Features:
 * - Proper landmark regions
 * - Skip to main content link
 * - Language attribute
 * - Proper heading hierarchy
 */
export function EmbedLayout({ children }: EmbedLayoutProps) {
  const settings = useWidgetSettings();
  const { config } = usePropertyContext();

  // Notify parent that widget is ready
  useEffect(() => {
    postMessageToParent(WIDGET_MESSAGE_TYPES.READY);
  }, []);

  // Notify parent of height changes
  useEffect(() => {
    // Initial height
    notifyHeightChange();

    // Watch for DOM changes
    const observer = new ResizeObserver(() => {
      notifyHeightChange();
    });

    observer.observe(document.body);

    return () => observer.disconnect();
  }, []);

  // Apply custom primary color as CSS variable
  useEffect(() => {
    document.documentElement.style.setProperty('--primary', hexToHsl(settings.primaryColor));
  }, [settings.primaryColor]);

  // Set document title for accessibility
  useEffect(() => {
    const propertyName = config?.property?.name || 'Experiences';
    document.title = `Book Experiences - ${propertyName}`;
  }, [config?.property?.name]);

  return (
    <div className="min-h-screen bg-transparent" lang="en">
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      <DemoBanner />
      
      <main 
        id="main-content" 
        className="p-4" 
        role="main"
        aria-label={`Experiences at ${config?.property?.name || 'this property'}`}
      >
        <div className="mx-auto max-w-6xl">
          {/* Hidden heading for screen readers */}
          <h1 className="sr-only">
            Book experiences at {config?.property?.name || 'this property'}
          </h1>
          
          {children}
        </div>

        {settings.showPoweredBy && (
          <footer className="mt-8 text-center" role="contentinfo">
            <a
              href="https://experiencelocal.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
            >
              Powered by ExperienceLocal
            </a>
          </footer>
        )}
      </main>
    </div>
  );
}

/**
 * Convert hex color to HSL values for CSS variable
 */
function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse hex
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
